"use client";

import { useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
export default function Evolution() {
  const container = useRef<HTMLElement>(null);
  const headlineWords = [{
    text: "Search",
    italic: false,
    marker: false
  }, {
    text: "Has",
    italic: false,
    marker: false
  }, {
    text: "Evolved",
    italic: false,
    marker: false
  }, {
    text: "Beyond",
    italic: true,
    marker: false
  }, {
    text: "Traditional",
    italic: true,
    marker: false
  }, {
    text: "Rankings",
    italic: true,
    marker: true
  }];
  useGSAP(() => {
    const el = container.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".vt-word", {
        y: 40
      });
      gsap.to(".vt-word", {
        opacity: 1,
        y: 0,
        duration: 0.9,
        ease: "power3.out",
        stagger: 0.07,
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
      gsap.to(".vt-marker-path", {
        strokeDashoffset: 0,
        duration: 0.6,
        ease: "power2.out",
        delay: 0.3,
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
      gsap.set([".vt-body", ".vt-meta", ".vt-cta"], {
        y: 20
      });
      gsap.to([".vt-body", ".vt-meta", ".vt-cta"], {
        opacity: 1,
        y: 0,
        duration: 0.7,
        ease: "power3.out",
        stagger: 0.1,
        scrollTrigger: {
          trigger: el,
          start: "top 75%",
          once: true
        }
      });
      gsap.set([".vt-photo-top", ".vt-photo-bot"], {
        y: 30
      });
      gsap.to([".vt-photo-top", ".vt-photo-bot"], {
        opacity: 1,
        y: 0,
        duration: 0.9,
        ease: "power3.out",
        stagger: 0.15,
        scrollTrigger: {
          trigger: el,
          start: "top 75%",
          once: true
        }
      });
      gsap.to(".vt-photo-top", {
        y: 20,
        ease: "none",
        scrollTrigger: {
          trigger: el,
          start: "top bottom",
          end: "bottom top",
          scrub: true
        }
      });
      gsap.to(".vt-photo-bot", {
        y: -20,
        ease: "none",
        scrollTrigger: {
          trigger: el,
          start: "top bottom",
          end: "bottom top",
          scrub: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".vt-word", ".vt-body", ".vt-meta", ".vt-cta", ".vt-photo-top", ".vt-photo-bot"], {
        opacity: 1,
        y: 0
      });
      gsap.set(".vt-marker-path", {
        strokeDashoffset: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: container
  });
  return <section data-section-id="30684" ref={container} id="ai-search-modern-seo-evolution" data-nav="dark" className="dark bg-background text-foreground relative overflow-hidden" style={{
    paddingTop: "clamp(128px, 16vh, 200px)",
    paddingBottom: "clamp(128px, 16vh, 200px)"
  }}>
      <div className="mx-auto w-full max-w-[1440px]" style={{
      paddingLeft: "clamp(24px, 5vw, 88px)",
      paddingRight: "clamp(24px, 5vw, 88px)"
    }}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <div className="font-sans text-muted-foreground uppercase mb-8" style={{
            fontSize: "clamp(0.8125rem, 0.88vw, 0.92rem)",
            letterSpacing: "0.18em",
            fontWeight: 500
          }}>
              AI SEARCH OPTIMISATION
            </div>

            <h2 className="font-serif text-foreground" style={{
            fontSize: "clamp(1.85rem, 2.8vw, 2.65rem)",
            fontWeight: 400,
            letterSpacing: "-0.028em",
            lineHeight: 0.98
          }}>
              <span className="inline !text-3xl md:!text-4xl">
                {headlineWords.map((w, i) => <span key={i} data-index={i} className="vt-word inline-block mr-[0.22em] relative" style={{
                opacity: 0
              }}>
                    <span className={w.italic ? "italic" : ""}>{w.text}</span>
                    {w.marker && <svg className="absolute left-0 right-0 pointer-events-none" style={{
                  bottom: "0.02em",
                  width: "100%",
                  height: "0.28em"
                }} viewBox="0 0 200 20" preserveAspectRatio="none" aria-hidden="true">
                        <path className="vt-marker-path" d="M2 12 C 30 6, 70 16, 110 10 S 180 8, 198 12" fill="none" stroke="hsl(340 75% 65%)" strokeWidth="6" strokeLinecap="round" pathLength={1} strokeDasharray={1} strokeDashoffset={1} />
                      </svg>}
                  </span>)}
              </span>
            </h2>

            <div className="mt-10 space-y-6" style={{
            maxWidth: "54ch"
          }}>
              <p className="vt-body font-sans text-foreground/85" style={{
              opacity: 0,
              fontSize: "clamp(1.25rem, 1.35vw, 1.5rem)",
              fontWeight: 400,
              lineHeight: 1.55,
              letterSpacing: "-0.005em"
            }}>
                Search visibility now extends beyond traditional results into
                AI-assisted discovery and answer experiences. As prospective
                clients consult Google AI Overviews, ChatGPT, Perplexity, and
                Gemini, search behavior is shifting from basic link clicking to
                synthesized answers.
              </p>
              <p className="vt-body font-sans text-foreground/85" style={{
              opacity: 0,
              fontSize: "clamp(1.25rem, 1.35vw, 1.5rem)",
              fontWeight: 400,
              lineHeight: 1.55,
              letterSpacing: "-0.005em"
            }}>
                I build sustainable presence through robust entity understanding,
                structured information, and authoritative content. By clarifying
                your topical depth and technical architecture, your site stays
                trusted across modern AI-assisted search platforms.
              </p>
            </div>

            <div className="vt-meta mt-10 space-y-2 font-sans text-muted-foreground uppercase" style={{
            opacity: 0,
            fontSize: "0.9375rem",
            letterSpacing: "0.06em",
            fontWeight: 500,
            lineHeight: 1.3
          }}>
              <div>TECHNICAL SEO</div>
              <div>AI MODEL ALIGNMENT</div>
              <div>SUSTAINABLE GROWTH</div>
            </div>

            <div className="vt-cta mt-12 flex flex-wrap items-center gap-6" style={{
            opacity: 0
          }}>
              <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 h-auto py-[18px] px-8 text-[17px] font-medium font-sans rounded-lg transition-colors duration-300" style={{
              letterSpacing: "0.02em"
            }}>
                <Link to="/ContactPage">Hire Me Directly</Link>
              </Button>
              <Link to="/ServicesPage" className="group inline-flex items-center gap-2 font-sans text-foreground text-[17px] font-medium relative" style={{
              letterSpacing: "0.02em"
            }}>
                <span className="relative">
                  Explore my services
                  <span className="absolute left-0 bottom-[-4px] h-px w-0 bg-accent transition-all duration-300 group-hover:w-full" />
                </span>
                <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-[6px]" />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="relative">
              <div className="vt-photo-top relative lg:ml-[30px] overflow-hidden" style={{
              opacity: 0,
              aspectRatio: "5 / 4"
            }}></div>
              <div className="vt-photo-bot relative lg:-mr-[30px] lg:ml-0 overflow-hidden" style={{
              opacity: 0,
              aspectRatio: "5 / 4",
              marginTop: "-60px"
            }}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/ChatGPT-Image-Sep-27-2026-10_24_57-PM.png" alt="Modern AI search optimisation and sustainable SEO strategy framework" loading="lazy" decoding="async" className="w-full h-full object-cover" style={{
                filter: "brightness(0.85)"
              }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}